/**
 * @file MUI theme for Unheard.FM (gem-app).
 * @copyright Copyright (c) 2026 Woolly Bear Software LLC, all rights reserved.
 * @overview Brand palette and typography; stock MUI component styling.
 */
import { keyframes } from "@emotion/react";
import { type SxProps, type Theme, createTheme } from "@mui/material/styles";

/** Rock back and forth ±40deg — used by the loading-indicator "knob" icon. */
export const rotateKnobKeyframes = keyframes`
  0% { transform: rotate(0deg); }
  50% { transform: rotate(-40deg); }
  100% { transform: rotate(40deg); }
`;

/** Full rotation — used by the minimal inline sync spinner. */
export const spinKeyframes = keyframes`
  100% { transform: rotate(360deg); }
`;

// Visually hide (screen-reader only) — same technique as Bootstrap's
// `.sr-only`/`.visually-hidden` / WAI-ARIA's visually-hidden pattern.
export const visuallyHiddenSx: SxProps<Theme> = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: -1,
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
};

/** Brand palette (legacy values from index.ejs). */
export const gemColors = {
  black1: "#303030",
  black2: "#282828",
  green: "#3B4237",
  lightGreen: "#9FAC90",
  darkGreen: "#293229",
  red: "#FF5E55",
  red2: "#FFC3BF",
  lightSuccess: "#8EBF87",
  success: "rgb(25, 135, 84)",
  secondary: "#7C8771",
  border: "rgba(0, 0, 0, 0.15)",
} as const;

/** Poppins — used on playlist export select until that view is restyled. */
export const gemFontPoppins = "'Poppins', sans-serif";

/**
 * Dark "strip" sub-theme for the sticky playlist header / source-dest strip
 * and its small pill controls — distinct from the main light brand palette.
 * Shared by playlist.js and now-playing.js.
 */
export const stripColors = {
  bg: "#485043",
  text: "#9FAC90",
  border: "rgba(159, 172, 144, 0.4)",
} as const;

export const controlPillSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  px: 1,
  py: 0.3,
  fontFamily: gemFontPoppins,
  fontSize: "0.7rem",
  fontWeight: 500,
  color: stripColors.text,
  backgroundColor: stripColors.bg,
  border: `1px solid ${stripColors.border}`,
  borderRadius: "9999px",
};

export const controlPillIconSx: SxProps<Theme> = {
  fontSize: "0.9rem",
  color: "rgba(159, 172, 144, 0.9)",
};

/**
 * Square (not pill-shaped) slide/tab nav buttons — overrides the app-wide
 * MuiButtonGroup pill theme (below) for the wizard step tabs used across
 * preferences.js, basic-preferences.tsx, quick-create.tsx, holiday-create.tsx,
 * discovery-wizard.tsx, and filters.tsx.
 */
export const slideNavButtonGroupSx: SxProps<Theme> = {
  mb: 2,
  "& .MuiButtonGroup-grouped": {
    borderRadius: "0 !important",
    padding: "8px",
    fontSize: "0.75rem",
  },
};

export const gemTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: gemColors.green,
      contrastText: "#fff",
    },
    secondary: {
      main: gemColors.secondary,
    },
    error: {
      main: gemColors.red,
    },
    success: {
      main: gemColors.success,
    },
    background: {
      default: gemColors.lightGreen,
      paper: gemColors.lightGreen,
    },
    text: {
      primary: gemColors.black1,
      secondary: "rgba(48, 48, 48, 0.6)",
    },
  },
  typography: {
    fontFamily: "'Figtree', sans-serif",
    h1: {
      fontFamily: "'Proza Libre', sans-serif",
      fontSize: "29px",
    },
    h2: {
      fontSize: "17px",
      textTransform: "uppercase",
      letterSpacing: "1.15px",
      color: gemColors.darkGreen,
      fontWeight: 700,
    },
    h3: {
      fontSize: "17px",
      textTransform: "uppercase",
      letterSpacing: "1.15px",
      color: gemColors.darkGreen,
      fontWeight: 700,
    },
    h4: {
      fontSize: "17px",
      textTransform: "uppercase",
      letterSpacing: "1.15px",
      color: gemColors.darkGreen,
      fontWeight: 700,
    },
    h5: {
      fontSize: "17px",
      textTransform: "uppercase",
      letterSpacing: "1.15px",
      color: gemColors.darkGreen,
      fontWeight: 700,
      opacity: 0.6,
    },
  },
  // shape: {
  //   borderRadius: 8,
  // },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          height: "100%",
        },
        body: {
          minHeight: "100%",
          margin: 0,
          backgroundColor: gemColors.lightGreen,
          color: gemColors.black1,
          // A few spots (e.g. track-row.tsx's edit-mode highlight and swap
          // overlay) intentionally break out of the app's centered Container
          // via the width:100vw full-bleed technique. 100vw includes the
          // vertical scrollbar's gutter, so without this it overflows the
          // actual visible width by a few pixels and forces an unwanted
          // horizontal scrollbar.
          overflowX: "hidden",
        },
        "#root": {
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        },
        // Browser default is a two-tone inset line; match the app's
        // standard border color instead.
        hr: {
          border: "none",
          borderTop: `1px solid ${gemColors.border}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    /**
     * The accordion root itself stays plain (no border/radius) — only the
     * header (MuiAccordionSummary, below) is pilled/bordered. The
     * app-specific "has a selection" highlight isn't a native MUI state,
     * so it stays local `sx` in category-select.tsx.
     */
    MuiAccordion: {
      styleOverrides: {
        root: {
          "&::before": {
            display: "none",
          },
          "&.Mui-expanded": {
            margin: 0,
          },
        },
      },
    },
    /** Pill-shaped, bordered accordion header (category-select.tsx). */
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          border: `1px solid ${gemColors.darkGreen}`,
          borderRadius: 999,
          "&.Mui-focusVisible": {
            outline: `2px solid ${gemColors.black2}`,
          },
          minHeight: "38px",
        },
        content: {
          margin: 0,
          "&.Mui-expanded": {
            margin: 0,
          },
        },
        // MUI hardcodes this to theme.palette.action.active by default,
        // which stops it from picking up the header's own `color` (e.g.
        // category-select.tsx's hasSelection lightGreen) via inheritance.
        expandIconWrapper: {
          color: "inherit",
        },
      },
    },
    /** Flat card (no default MUI elevation shadow) — see welcome.tsx. */
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          textTransform: "uppercase",
        },
      },
    },
    /**
     * Pill-shaped segmented button groups (slide-selector tabs, back/next
     * action bars — used across most of the preferences/wizard views),
     * matching the pill motif used elsewhere (accordions, chips, option
     * buttons).
     */
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: 999,
        },
        grouped: {
          "&:first-of-type": {
            borderTopLeftRadius: 999,
            borderBottomLeftRadius: 999,
          },
          "&:last-of-type": {
            borderTopRightRadius: 999,
            borderBottomRightRadius: 999,
          },
        },
      },
    },
    /**
     * App-wide content width cap — matches the old Bootstrap `.container`
     * (720px), not MUI's much wider default `maxWidth="lg"` (1200px).
     */
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: "720px",
          "@media (min-width: 1200px)": {
            maxWidth: "720px",
          },
        },
      },
    },
  },
});
